import { useId, useState } from "react";

import type { TimelineItemOut } from "@/api";
import { SystemIcon } from "@/components/icons";
import { formatDateTime } from "@/lib/format";

import { bundleTitle } from "./bundle";
import { EntryTime, MeasureText, NBSP } from "./EntryParts";
import { Fold } from "./Fold";
import styles from "./Record.module.css";

/**
 * A run of one person's work events on one day, in one line (design
 * 2026-10-24 §6.3, §8.4; `bundle.ts`): what the run did, by kind —
 * „Planung bearbeitet · 6 Stände, 2 Maße korrigiert" (`bundleTitle`,
 * UX-Gutachten 1, B-18) — with who, and „+n weitere" beside the name. Open,
 * its lines under it, indented, each with its time in the column the
 * entries' times stand in; since the title quotes none of them, all of them,
 * the newest first. A run the title cannot name keeps its newest line word
 * for word as the head, and the rest under it (§8.4). „+n weitere" counts
 * the lines the toggle opens. The line is as quiet as one system event; it
 * stands for several.
 */
export function EventBundle({ items }: { items: readonly TimelineItemOut[] }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const events = items.flatMap((item) => (item.kind === "event" ? [item.event] : []));
  const [newest] = events;
  if (newest === undefined) return null;
  const title = bundleTitle(items);
  const lines = title !== null ? events : events.slice(1);
  const heading = title ?? newest.summary;
  return (
    <article
      className={`${styles.entry} ${styles.system} ${styles.bundle}`}
      aria-label={`${heading} · ${lines.length} weitere`}
    >
      <span className={styles.icon} aria-hidden="true">
        <SystemIcon size={14} />
      </span>
      <p className={styles.systemLine}>
        <span className="visually-hidden">Systemereignis: </span>
        {title ?? <MeasureText text={newest.summary} />}
        {/* The toggle rides with the name: wide on the sentence's line, on a
            phone on the name's own line (R3-7). */}
        <span className={styles.systemActor}>
          <span className={styles.systemDot}>{NBSP}· </span>
          {newest.actorDisplayName}{" "}
          {/* Named with what it opens (final review, M-13): „+4 weitere"
              alone said nothing out of its line. The visible words lead the
              name, so a voice command that says them still finds it. */}
          <button
            type="button"
            className={styles.runToggle}
            aria-expanded={open}
            aria-controls={id}
            aria-label={open ? `Weniger zeigen: ${heading}` : `+${lines.length} weitere Einträge: ${heading}`}
            onClick={() => setOpen((shown) => !shown)}
          >
            {open ? "Weniger zeigen" : `+${lines.length} weitere`}
          </button>
        </span>
      </p>
      <div className={styles.entrySide}>
        <EntryTime at={newest.at} title={formatDateTime(newest.at)} />
        <span className={styles.menuSlot} aria-hidden="true" />
      </div>
      {/* A row of its own under the line, across text and time. */}
      <Fold id={id} open={open} className={styles.runFold}>
        <ol className={styles.runLines}>
          {lines.map((event) => (
            <li key={event.id} className={styles.runLine}>
              <span className={styles.runText}>
                <MeasureText text={event.summary} />
              </span>
              <EntryTime at={event.at} title={formatDateTime(event.at)} />
            </li>
          ))}
        </ol>
      </Fold>
    </article>
  );
}
